import {
  createContext,
  useContext,
  useEffect,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNotify } from '@/components/Notify'
import { PlaybackController, type PlayerActions, type PlayerUiState } from './controller'
import { useMediaSession } from './useMediaSession'

// Действия и состояние разнесены по двум контекстам, как в Notify: страницы, которым нужен
// только play(episode), подписаны на стабильный объект действий и не ре-рендерятся при
// смене эпизода/режима. Частое время в контекст не попадает вовсе (см. PlayerUiState).
const PlayerActionsCtx = createContext<PlayerActions | null>(null)
const PlayerStateCtx = createContext<PlayerUiState | null>(null)

// Монтируется в AppLayout НАД key={pathname}-узлом (как JobsStreamProvider): смена маршрута
// не трогает ни контроллер, ни медиа-элемент в PlayerDock — воспроизведение переживает
// навигацию. Размонтирование (logout) — стоп и очистка src.
export function PlayerProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const { show } = useNotify()
  const [controller] = useState(() => new PlaybackController(queryClient, show))
  const ui = useSyncExternalStore(controller.subscribe, controller.getUi)

  useEffect(() => controller.mount(), [controller])
  useMediaSession(ui.episode, controller.mediaSessionHandlers)

  return (
    <PlayerActionsCtx.Provider value={controller.actions}>
      <PlayerStateCtx.Provider value={ui}>{children}</PlayerStateCtx.Provider>
    </PlayerActionsCtx.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function usePlayerActions(): PlayerActions {
  const ctx = useContext(PlayerActionsCtx)
  if (!ctx) throw new Error('usePlayerActions must be used inside <PlayerProvider>')
  return ctx
}

// eslint-disable-next-line react-refresh/only-export-components
export function usePlayerState(): PlayerUiState {
  const ctx = useContext(PlayerStateCtx)
  if (!ctx) throw new Error('usePlayerState must be used inside <PlayerProvider>')
  return ctx
}
